import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { photoUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";
import { Bird, AlertTriangle, MapPin, HeartHandshake, ShieldCheck } from "lucide-react";

export default function BirdProfile() {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ message: "", contact: "", location: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/tags/${code}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [code]);

  const send = async () => {
    if (!form.message) return toast.error("Descreva onde/como encontrou a ave");
    setSending(true);
    try {
      await api.post(`/tags/${code}/found`, form);
      toast.success("Proprietário avisado! Obrigado por ajudar.");
      setOpen(false);
    } catch {
      toast.error("Erro ao enviar");
    } finally {
      setSending(false);
    }
  };

  if (error) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Anilha não encontrada.</div>;
  if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-muted-foreground">Carregando...</div>;

  const pet = data.pet;
  const lost = pet?.lost?.active;

  const fields = pet ? [
    ["Nome", pet.name],
    ["Espécie / Raça", pet.breed || pet.species],
    ["Sexo", pet.sex || "Não informado"],
    ["Data de nascimento", pet.birthdate ? pet.birthdate.split("-").reverse().join("/") : "Não informada"],
    ["Identificação", data.tag_code],
    ["Proprietário", data.tutor_name || "Não informado"],
    ["Criador / Origem", pet.creator || "Não informado"],
    ["Status", pet.bird_status || "Ativa"],
  ] : [];

  return (
    <div className="max-w-lg mx-auto px-6 py-12" data-testid="bird-profile-page">
      {lost && (
        <div className="bg-red-600 text-white rounded-2xl p-5 mb-6 text-center animate-pulse" data-testid="bird-lost-banner">
          <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
          <p className="font-heading font-bold text-xl">ESTA AVE ESTÁ DESAPARECIDA</p>
          {pet.lost.location && <p className="text-white/70 text-xs mt-1 flex items-center justify-center gap-1"><MapPin className="w-3 h-3" /> Último local: {pet.lost.location}</p>}
        </div>
      )}

      <div className="bg-white rounded-3xl overflow-hidden border shadow-sm">
        <div className="bg-brand-graphite py-4 text-center">
          <span className="inline-flex items-center gap-2 text-brand-mint text-xs font-semibold tracking-wide uppercase">
            <Bird className="w-4 h-4" /> PetMachine Hub — Identificação para Aves
          </span>
        </div>
        {pet?.photo ? (
          <img src={photoUrl(pet.photo)} alt={pet.name} className="w-full h-72 object-cover" />
        ) : (
          <div className="w-full h-48 bg-brand-mintLight flex items-center justify-center"><Bird className="w-14 h-14 text-brand-mint" /></div>
        )}
        <div className="p-7">
          {pet ? (
            <>
              <h1 className="font-heading font-extrabold text-3xl text-brand-graphite text-center">{pet.name}</h1>
              <p className="text-center text-xs font-mono text-muted-foreground mt-1">{data.tag_code}</p>
              <div className="mt-6 divide-y" data-testid="bird-fields">
                {fields.map(([label, value]) => (
                  <div key={label} className="flex items-center justify-between py-3">
                    <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
                    <span className="text-sm font-semibold text-brand-graphite text-right">{value}</span>
                  </div>
                ))}
              </div>
              {pet.notes && (
                <div className="mt-4 bg-brand-cream rounded-xl p-4">
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Observações</p>
                  <p className="text-sm text-brand-graphite">{pet.notes}</p>
                </div>
              )}
            </>
          ) : (
            <p className="text-center text-sm text-muted-foreground py-6">Esta anilha ainda não foi vinculada a uma ave.</p>
          )}

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className={`w-full rounded-full h-12 mt-7 font-semibold ${lost ? "bg-red-600 hover:bg-red-700" : "bg-brand-mint hover:bg-brand-mintDark"} text-white`} data-testid="bird-contact-btn">
                <HeartHandshake className="w-5 h-5 mr-2" /> {lost ? "Encontrei esta ave!" : "Contatar o proprietário"}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Contatar o proprietário</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Textarea placeholder="Ex: Encontrei esta calopsita no bairro Saguaçu..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="bird-found-message" />
                <Input placeholder="Local (bairro/região)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="bird-found-location" />
                <Input placeholder="Seu contato (telefone/WhatsApp)" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="bird-found-contact" />
                <Button onClick={send} disabled={sending} className="w-full bg-brand-coral hover:bg-brand-coralDark text-white rounded-full" data-testid="bird-found-submit">
                  {sending ? "Enviando..." : "Enviar ao proprietário"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <p className="flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground mt-5 text-center">
            <ShieldCheck className="w-3.5 h-3.5 text-brand-mint shrink-0" />
            O proprietário define quais informações ficam públicas neste perfil.
          </p>
        </div>
      </div>
      <p className="text-center text-xs text-muted-foreground mt-6">Anilha 2D Inteligente — petmachinehub.com.br</p>
    </div>
  );
}
